import noop from 'lodash/noop';
import React, { useState } from 'react';
import ReactDiffViewer from 'react-diff-viewer';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  H2,
  H4,
  H5,
  HStack,
  Heading,
  Label,
  P,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  VStack,
} from '@eluve/components';
import {
  Block,
  ClassicSummary,
  DynamicArtifactTemplate,
  DynamicSummary,
  dynamicArtifactTemplateSchema,
  flattenBlocks,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import { SummarySectionMarkdown } from '@eluve/smart-blocks';
import { Translator } from '@eluve/translation';
import {
  AppointmentSummaryKeys,
  summaryKeyToSectionTitleMap,
} from '@eluve/utils';
import {
  BillingCode,
  getVendorDataMapper,
  isClassicSummary,
  isDynamicSummary,
} from '@eluve/vendor';

import { DiffExportButton } from './DiffExportButton';
import { DiffViewer } from './DiffViewer';
import { ehrDiffContentFragment } from './operations';

export const EhrDiffContent: React.FC = () => {
  const { appointmentId, tenantId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };
  const appointment = useCompleteFragment({
    fragment: ehrDiffContentFragment,
    key: { id: appointmentId },
  });
  const humanSoapNote = appointment?.humanOutputs?.[0];
  const humanSoapNoteContent = humanSoapNote?.output?.content;
  const { additionalNotes } = appointment?.doctor_interaction ?? {};

  const outputVariant =
    humanSoapNote?.output?.llm_output?.metadata?.output_variant;

  let hydratedHumanOutput: DynamicArtifactTemplate | undefined = undefined;
  if (outputVariant) {
    const artifactTemplate = dynamicArtifactTemplateSchema.parse(
      outputVariant.template,
    );
    hydratedHumanOutput = hydrateDynamicArtifactTemplate(
      artifactTemplate,
      humanSoapNoteContent ?? {},
    );
  }

  let ehrSignedNote: ClassicSummary | DynamicSummary | undefined;
  let signedBillingCodes: BillingCode[] | undefined;
  let chartParts: Record<string, unknown>[] = [];
  const externalChart = appointment?.external_charts?.[0];
  const externalEhrVendor = externalChart?.external_ehr?.vendor;
  if (externalEhrVendor) {
    const vendorProvider = getVendorDataMapper(externalEhrVendor);
    if (vendorProvider.convertChartToSummary) {
      const artifactTemplate = outputVariant
        ? dynamicArtifactTemplateSchema.parse(outputVariant.template)
        : undefined;

      if (artifactTemplate) {
        const convertedSummary = vendorProvider.convertChartToSummary({
          data: externalChart?.rawData ?? {},
          template: artifactTemplate,
        });
        if (convertedSummary) {
          ehrSignedNote = convertedSummary.summary;
          signedBillingCodes = convertedSummary.billingCodes;
        }
      }
    }
    if (vendorProvider.convertChartToParts) {
      chartParts = vendorProvider.convertChartToParts(externalChart?.rawData);
    }
  }

  const [translatedContent, setTranslatedContent] = useState<string | null>(
    null,
  );

  const humanBillingCodes: BillingCode[] = appointment.billingCodes?.map(
    (bc: any) => ({
      code: bc.medical_code.code,
      description: bc.medical_code.description,
    }),
  );

  // Full Content for translation
  const toTranslate = {
    humanSoapNoteContent,
    ehrSignedNote,
    humanBillingCodes,
    signedBillingCodes,
  };

  const parsedTranslatedContent: typeof toTranslate = translatedContent
    ? JSON.parse(translatedContent)
    : null;

  const translatedHumanSoapNoteContent =
    parsedTranslatedContent?.humanSoapNoteContent;
  const translatedHumanBillingCodes =
    parsedTranslatedContent?.humanBillingCodes;
  const translatedEhrSignedNote = parsedTranslatedContent?.ehrSignedNote;
  const translatedSignedBillingCodes =
    parsedTranslatedContent?.signedBillingCodes;

  // Hydrate translated humanSoapNoteContent if available
  let translatedHydratedHumanOutput: DynamicArtifactTemplate | undefined =
    undefined;
  if (translatedHumanSoapNoteContent && hydratedHumanOutput && outputVariant) {
    const artifactTemplate = dynamicArtifactTemplateSchema.parse(
      outputVariant.template,
    );
    translatedHydratedHumanOutput = hydrateDynamicArtifactTemplate(
      artifactTemplate,
      translatedHumanSoapNoteContent ?? {},
    );
  }

  const displayHydratedHumanOutput =
    translatedHydratedHumanOutput ?? hydratedHumanOutput;
  const displayHumanSoapNoteContent =
    translatedHumanSoapNoteContent ?? humanSoapNoteContent;
  const displayHumanBillingCodes =
    translatedHumanBillingCodes ?? humanBillingCodes;
  const displayEhrSignedNote = translatedEhrSignedNote ?? ehrSignedNote;

  const displaySignedBillingCodes =
    translatedSignedBillingCodes ?? signedBillingCodes;

  // Prepare blocks for export
  let humanBlocks: Block[] | undefined;
  let ehrBlocks: Block[] | undefined;
  if (
    displayHydratedHumanOutput &&
    displayEhrSignedNote &&
    isDynamicSummary(displayEhrSignedNote)
  ) {
    humanBlocks = flattenBlocks(displayHydratedHumanOutput.blocks);
    ehrBlocks = flattenBlocks(displayEhrSignedNote.blocks);
  }

  return (
    <VStack>
      <HStack className="mt-4" justify="between" align="center">
        <Heading>Summary</Heading>
        <DiffExportButton
          appointmentId={appointmentId}
          tenantId={tenantId}
          type="ehr"
        />
      </HStack>

      <div className="mb-4 w-1/3">
        <Label>Need translation?</Label>
        <Translator
          originalContent={JSON.stringify(toTranslate)}
          setTranslatedContent={setTranslatedContent}
        />
      </div>

      {displayEhrSignedNote &&
        isDynamicSummary(displayEhrSignedNote) &&
        displayHydratedHumanOutput && (
          <VStack gap={8}>
            {(() => {
              if (!humanBlocks || !ehrBlocks) {
                return null;
              }
              return humanBlocks.map((humanOutputBlock) => {
                const matchingExternalEhrBlock = ehrBlocks.find((b) =>
                  b.key
                    .toLowerCase()
                    .includes(humanOutputBlock.key.toLowerCase()),
                );
                if (matchingExternalEhrBlock) {
                  return (
                    <VStack
                      key={humanOutputBlock.label}
                      align="stretch"
                      gap={4}
                    >
                      <H2>{humanOutputBlock.label}</H2>
                      <DiffViewer
                        newBlock={matchingExternalEhrBlock}
                        oldBlock={humanOutputBlock}
                      />
                    </VStack>
                  );
                }
                return null;
              });
            })()}
          </VStack>
        )}
      {displayEhrSignedNote && isClassicSummary(displayEhrSignedNote) && (
        <VStack gap={8}>
          {Object.entries(summaryKeyToSectionTitleMap).map(
            ([key, sectionTitle]) => {
              const summaryKey = key as AppointmentSummaryKeys;
              const oldValue =
                displayHumanSoapNoteContent?.[summaryKey] ??
                displayHumanSoapNoteContent?.[summaryKey.toLowerCase()] ??
                '';
              const newValue = displayEhrSignedNote?.data?.[summaryKey] ?? '';
              return (
                <VStack key={summaryKey} align="stretch" gap={4}>
                  <H5>{sectionTitle}</H5>
                  <ReactDiffViewer
                    oldValue={
                      typeof oldValue === 'string'
                        ? oldValue
                        : JSON.stringify(oldValue, null, 2)
                    }
                    newValue={
                      typeof newValue === 'string'
                        ? newValue
                        : JSON.stringify(newValue, null, 2)
                    }
                    splitView={true}
                    showDiffOnly={true}
                  />
                </VStack>
              );
            },
          )}
        </VStack>
      )}

      {additionalNotes && (
        <SummarySectionMarkdown
          key="AdditionalNotes"
          summaryKey={`AdditionalNotes` as any}
          sectionTitle="Additional Notes"
          content={additionalNotes}
          disabled={true}
          handleContentChange={noop}
        />
      )}
      {Boolean(chartParts?.length) && (
        <div>
          <H2>View Raw EHR Chart</H2>
          {chartParts.map((part, index) => {
            return (
              <div key={`chart-part-${index}`} className="my-4">
                <Table className="min-w-full divide-y divide-border-primary">
                  <TableHeader className="bg-bg-secondary">
                    <H4 cn="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-supporting">
                      Chart Entry
                    </H4>
                  </TableHeader>
                  <TableBody className="divide-y divide-border-primary bg-white">
                    {Object.entries(part).map(([key, value], rowIndex) => (
                      <TableRow
                        key={`row-${index}-${rowIndex}`}
                        className={
                          rowIndex % 2 === 0 ? 'bg-bg-secondary' : 'bg-white'
                        }
                      >
                        <TableCell className="whitespace-nowrap px-6 py-4 text-sm font-medium text-text-primary">
                          <P>{key}</P>
                        </TableCell>
                        <TableCell className="whitespace-nowrap px-6 py-4 text-sm text-text-supporting">
                          <P>{JSON.stringify(value)}</P>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })}
        </div>
      )}
      <HStack>
        <Heading>Billing Codes</Heading>
      </HStack>
      {Boolean(appointment.billingCodes?.length) && (
        <HStack justify="between">
          <ReactDiffViewer
            oldValue={(displayHumanBillingCodes ?? [])
              .map(
                (billingCode: BillingCode) =>
                  `${billingCode.code} - ${billingCode.description}`,
              )
              .join('\n')}
            newValue={(displaySignedBillingCodes ?? [])
              .map(
                (billingCode: BillingCode) =>
                  `${billingCode.code} - ${billingCode.description}`,
              )
              .join('\n')}
            splitView={true}
          />
        </HStack>
      )}
    </VStack>
  );
};
